import { Components } from '@/api/generated/api';
import type { IConfirmDialogProps } from '@/composables/useConfirmDialog';
import { PerformanceAssessmentEntryObservationStatus } from '@/core/performanceAssessments/performanceAssessmentEntryObservationStatus';
import type { TTranslate } from '@/interfaces';
import { clampNumber } from 'common/utils/math.utils';
import { getIsDomainMatchingSubject } from '@/utils/schoolSubject.utils';
import { EStatisticsCalculationType } from 'common/enum/misc.enum';
import { immutableSort } from 'common/utils/array.utils';
import { belongsToSchoolTerm } from 'common/utils/date.utils';
import { nonEmpty } from 'common/utils/string.utils';

type TPerformanceLevelWithImage = {
  picturePath?: string | null;
  pictureUrl?: string | null;
};

type TPerformanceAssessmentDropScope = Pick<
  Components.Schemas.PerformanceAssessmentDto,
  'schoolClassId' | 'schoolLevel' | 'schoolSubjectId' | 'isTemplate'
>;

type TPerformanceAssessmentUpdateSource = Pick<
  Components.Schemas.PerformanceAssessmentDto,
  'name' | 'symbol' | 'documentationMode' | 'performanceVisualId' | 'schoolLevel' | 'weighting' | 'schoolClassId'
>;

type TPerformanceAssessmentSubjectScope = Pick<
  Components.Schemas.BasePerformanceAssessmentDto,
  'id' | 'order' | 'schoolSubjectId'
>;

type TPerformanceAssessmentStatLike = {
  ratingAmount: number;
  level: {
    level: number;
  };
};

interface IBuildPerformanceAssessmentDeleteConfirmationParams {
  assessmentName: string;
  confirmationValue: string;
  entryCount: number;
  t: TTranslate;
}

export const getPerformanceLevelImageUrl = (level?: TPerformanceLevelWithImage | null): string | null => {
  return level?.pictureUrl ?? level?.picturePath ?? null;
};

/**
 * Display name for a performance visual. Official/provider visuals carry a language-agnostic `key` and are localized
 * here via `visualCatalog.<key>`; tenant-owned custom (uploaded) visuals have no key and use their stored `name`.
 */
export const getVisualDisplayName = (visual: { key?: string | null; name: string }, t: TTranslate): string =>
  visual.key ? t(`visualCatalog.${visual.key}`) : visual.name;

/**
 * Display name for a performance assessment. Official assessments carry a stable `key` and, while their name has not
 * been customized, are localized in the UI locale via `performanceAssessmentCatalog.<key>` — exactly like visuals.
 * Once a user renames an official assessment (`isNameCustomized`), or for tenant-authored ones without a `key`, the
 * stored `name` is shown verbatim.
 */
export const getPerformanceAssessmentName = (
  assessment: { key?: string | null; name: string; isNameCustomized?: boolean },
  t: TTranslate,
): string =>
  assessment.key && !assessment.isNameCustomized
    ? t(`performanceAssessmentCatalog.${assessment.key}`)
    : assessment.name;

export const buildPerformanceAssessmentDeleteConfirmation = (
  params: IBuildPerformanceAssessmentDeleteConfirmationParams,
): IConfirmDialogProps => ({
  title: params.t('performanceAssessment.removeTitle', { name: params.assessmentName }),
  description:
    params.entryCount > 0
      ? params.t(
          params.entryCount === 1
            ? 'performanceAssessment.removeWithOneEntryConfirmText'
            : 'performanceAssessment.removeWithEntriesConfirmText',
          { count: params.entryCount },
        )
      : params.t('performanceAssessment.removeConfirmText', { name: params.assessmentName }),
  confirmButtonLabel: params.t('general.remove'),
  declineButtonLabel: params.t('general.cancel'),
  theme: 'danger',
  size: 'xl',
  buttonAlignment: 'end',
  ...(params.entryCount > 0 && {
    checkInput: {
      inputPlaceholder: params.t('performanceAssessment.removeWithEntriesNamePlaceholder'),
      inputCheckValue: params.confirmationValue,
    },
  }),
});

/**
 * Official grade-scale level names carry a language-agnostic `key` (e.g. 'very-good', 'insufficient') and, unless the
 * user renamed the level (`isNameCustomized`), are localized via `gradeScaleLevelCatalog.<key>` — exactly like
 * assessments/visuals. Custom or legacy levels (no `key`) show the stored `name` verbatim.
 */
export const getGradeScaleLevelName = (
  level: { key?: string | null; name: string; isNameCustomized?: boolean },
  t: TTranslate,
): string => (level.key && !level.isNameCustomized ? t(`gradeScaleLevelCatalog.${level.key}`) : level.name);

type TCompetenceAssessmentLevelTextLike = {
  key?: string | null;
  isCustomized?: boolean;
  title: string | null;
  description: string | null;
};

export type TCompetenceAssessmentLevelTextDisplay = Pick<TCompetenceAssessmentLevelTextLike, 'title' | 'description'>;

export const getCompetenceAssessmentLevelTextCatalogEntry = (
  key: string,
  t: TTranslate,
): TCompetenceAssessmentLevelTextDisplay => ({
  title: t(`competenceAssessmentLevelTextCatalog.${key}.title`),
  description: t(`competenceAssessmentLevelTextCatalog.${key}.description`),
});

export const getCompetenceAssessmentLevelTextDisplay = (
  levelText: TCompetenceAssessmentLevelTextLike,
  t: TTranslate,
): TCompetenceAssessmentLevelTextDisplay =>
  levelText.key && !levelText.isCustomized
    ? getCompetenceAssessmentLevelTextCatalogEntry(levelText.key, t)
    : { title: levelText.title, description: levelText.description };

export const hasAnyCompetenceAssessmentLevelText = (
  levelTexts: TCompetenceAssessmentLevelTextLike[],
  t: TTranslate,
): boolean =>
  levelTexts.some((levelText) => {
    const display = getCompetenceAssessmentLevelTextDisplay(levelText, t);
    return nonEmpty(display.title) || nonEmpty(display.description);
  });

export const getHighestPerformanceLevelImageUrl = (
  performanceVisual?: Components.Schemas.PerformanceVisualDto,
): string | null => {
  if (!performanceVisual?.performanceLevelsV2.length) return null;

  const highestLevel = performanceVisual.performanceLevelsV2.reduce((highest, current) =>
    current.level > highest.level ? current : highest,
  );
  return getPerformanceLevelImageUrl(highestLevel);
};

export const getPerformanceLevelsSortedByLevels = (
  performanceVisual?: Components.Schemas.PerformanceVisualDto | null,
): Components.Schemas.PerformanceLevelV2Dto[] => {
  if (!performanceVisual) return [];
  return immutableSort(performanceVisual.performanceLevelsV2, (a, b) => a.level - b.level);
};

export const normalizePerformanceAssessmentOrder = <
  T extends Pick<Components.Schemas.BasePerformanceAssessmentDto, 'id' | 'order'>,
>(
  assessments: T[],
): T[] => {
  return [...assessments]
    .sort((a, b) => a.order - b.order || a.id - b.id)
    .map((assessment, index) => ({
      ...assessment,
      order: index,
    }));
};

export const getPerformanceAssessmentsForSchoolSubject = <T extends TPerformanceAssessmentSubjectScope>(
  assessments: T[],
  schoolSubject: Pick<Components.Schemas.SchoolSubjectDto, 'id' | 'useSubjectSpecificAssessments'> | null,
): T[] => {
  if (!schoolSubject) return [];

  const filtered = assessments.filter((assessment) => {
    if (schoolSubject.useSubjectSpecificAssessments) {
      return assessment.schoolSubjectId === schoolSubject.id;
    }

    return assessment.schoolSubjectId == null;
  });

  return normalizePerformanceAssessmentOrder(filtered);
};

export const isInSamePerformanceAssessmentDropScope = (
  source: TPerformanceAssessmentDropScope,
  target: TPerformanceAssessmentDropScope,
): boolean => {
  return (
    source.schoolClassId === target.schoolClassId &&
    source.schoolLevel === target.schoolLevel &&
    source.schoolSubjectId === target.schoolSubjectId &&
    source.isTemplate === target.isTemplate
  );
};

export const buildPerformanceAssessmentUpdateDto = (
  performanceAssessment: TPerformanceAssessmentUpdateSource,
  overrides: Partial<Components.Schemas.UpdatePerformanceAssessmentDto> = {},
): Components.Schemas.UpdatePerformanceAssessmentDto => ({
  name: performanceAssessment.name,
  symbol: performanceAssessment.symbol,
  performanceVisualId: performanceAssessment.performanceVisualId,
  documentationMode: performanceAssessment.documentationMode,
  schoolLevel: performanceAssessment.schoolLevel,
  weighting: performanceAssessment.weighting,
  schoolClassId: performanceAssessment.schoolClassId ?? undefined,
  ...overrides,
});

export const getPerformanceAssessmentValuationPercentage = <TStat extends TPerformanceAssessmentStatLike>(
  stats: TStat[],
): number => {
  const total = stats.reduce((sum, stat) => sum + stat.ratingAmount, 0);
  if (!total) return 0;

  const levels = stats.map((stat) => stat.level.level);
  if (!levels.length) return 0;

  const minLevel = Math.min(...levels);
  const maxLevel = Math.max(...levels);
  if (maxLevel <= minLevel) return 100;

  const weightedLevel = stats.reduce((sum, stat) => sum + stat.level.level * stat.ratingAmount, 0) / total;
  const clampedWeightedLevel = clampNumber(weightedLevel, minLevel, maxLevel);

  return ((clampedWeightedLevel - minLevel) / (maxLevel - minLevel)) * 100;
};

/**
 * Get the amount of observations
 * - filter by subject ids
 * - filter by term
 * - filter by calculation type (LAST_HISTORICAL_ENTRY = is the newest observation for one competence and student, ALL_HISTORICAL_ENTRIES = all observations are considered!)
 */

export const getFilteredObservationAmount = (params: {
  availableCompetenceDomains: Components.Schemas.CompetenceDomainDto[];
  observations: Components.Schemas.CompetenceObservationDto[];
  subjectIds: number[];
  term?: Components.Schemas.SchoolTermDto | null;
  calculationType?: EStatisticsCalculationType;
}): number => {
  const { term, subjectIds, observations, availableCompetenceDomains, calculationType } = params;

  let filtered = observations;

  if (subjectIds.length) {
    filtered = filtered.filter((observation) => {
      return subjectIds.some((subjectId) =>
        getIsDomainMatchingSubject(observation.competenceId, availableCompetenceDomains, subjectId),
      );
    });
  }

  if (term) {
    filtered = filtered.filter((observation) => {
      const observedAt = new Date(observation.observedAt);
      return belongsToSchoolTerm({
        val: observedAt,
        term,
      });
    });
  }

  if (calculationType === EStatisticsCalculationType.LAST_HISTORICAL_ENTRY) {
    const latestByStudentAndCompetence = new Map<string, Components.Schemas.CompetenceObservationDto>();

    for (const obs of filtered) {
      const key = `${obs.studentId}-${obs.competenceId}`;
      const existing = latestByStudentAndCompetence.get(key);

      if (!existing || new Date(obs.observedAt) > new Date(existing.observedAt)) {
        latestByStudentAndCompetence.set(key, obs);
      }
    }

    return latestByStudentAndCompetence.size;
  }

  return filtered.length;
};

export type TRatable = Extract<
  Components.Schemas.CompetenceObservationDto | Components.Schemas.PerformanceAssessmentStudentEntryDto,
  { performanceLevelV2Id: number | null }
>;

export const getObservationStatus = <T extends { performanceLevelV2Id: number | null; gradeValue?: number | null }>(
  items: T[],
): PerformanceAssessmentEntryObservationStatus => {
  const anyObserved = items.some((i) => i.performanceLevelV2Id != null || i.gradeValue != null);
  const allObserved = items.every((i) => i.performanceLevelV2Id != null || i.gradeValue != null);

  if (allObserved) return PerformanceAssessmentEntryObservationStatus.FullyObserved;
  if (anyObserved) return PerformanceAssessmentEntryObservationStatus.PartiallyObserved;
  return PerformanceAssessmentEntryObservationStatus.NotObserved;
};
